import { Box, CircularProgress } from '@mui/joy'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { verifyAuth, loadDebugData, selectIsLoading } from '../store/authSlice'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../store'
import { AGREEMENT_SIGN_PATH } from '../utils/secretLinkPage'

interface AuthInitializerProps {
    children: React.ReactNode
}

export const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
    const dispatch = useDispatch<AppDispatch>()
    const isLoading = useSelector(selectIsLoading)
    const [initialized, setInitialized] = useState(false)
    const location = useLocation()

    // Check if current path is a public route that handles its own auth
    // NOTE: Don't blanket-skip *all* `/onboarding/*` routes — some onboarding pages (e.g.
    // `/onboarding/tms/welcome`) are rendered inside the main app and rely on Redux auth.
    // Only the onboarding/hub/tms pre-auth flows should bypass `verifyAuth()` here.
    const isPublicOnboardingRoute =
        location.pathname === '/onboarding' ||
        location.pathname.startsWith('/onboarding/verify') ||
        location.pathname.startsWith('/onboarding/create-password') ||
        location.pathname.startsWith('/onboarding/login') ||
        location.pathname.startsWith('/onboarding/forgot-password') ||
        location.pathname.startsWith('/onboarding/connect-email') ||
        location.pathname.startsWith('/onboarding/email-template') ||
        location.pathname.startsWith('/onboarding/hub/') ||
        location.pathname.startsWith('/onboarding/tms/company-details')

    const isPublicRoute =
        location.pathname.startsWith('/ext-v2') ||
        location.pathname === '/ext' ||
        location.pathname.startsWith('/ext/') ||
        location.pathname.startsWith('/auth/') ||
        isPublicOnboardingRoute ||
        location.pathname.startsWith('/extension/') ||
        location.pathname.startsWith('/twa/') ||
        location.pathname.startsWith('/r/') ||
        location.pathname.startsWith('/broker') ||
        location.pathname.startsWith(AGREEMENT_SIGN_PATH)

    useEffect(() => {
        // Skip auth initialization for public routes - they handle their own auth
        if (isPublicRoute) {
            setInitialized(true)
            return
        }

        const initialize = async () => {
            await dispatch(verifyAuth())
            dispatch(loadDebugData())
            setInitialized(true)
        }

        initialize()
    }, [dispatch, isPublicRoute])

    // Skip loading state for public routes
    if (isPublicRoute) {
        return <>{children}</>
    }

    if (isLoading && !initialized) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                }}
            >
                <CircularProgress />
            </Box>
        )
    }

    return <>{children}</>
}
